import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ReferentialService } from './referential.service';

export type AppLanguage = string;

export interface LanguageOption {
  code: AppLanguage;
  label: string;
}

const STORAGE_KEY = 'ycc_lang';

/** Repli hors-ligne / avant résolution du catalogue backend. */
const FALLBACK_LANGUAGES: LanguageOption[] = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'bass', label: 'Bassa' },
  { code: 'dua', label: 'Duala' },
  { code: 'ewo', label: 'Ewondo' },
  { code: 'ful', label: 'Fulfulde' },
  { code: 'bum', label: 'Bamoun' },
  { code: 'bul', label: 'Bulu' },
  { code: 'gha', label: 'Ghomala' },
  { code: 'lam', label: 'Lamnso' },
];

/**
 * Langues pour lesquelles un bundle de traduction existe (public/i18n/*.json).
 * Les 8 langues nationales camerounaises (Bassa, Duala, Ewondo, Fulfulde, Bamoun, Bulu,
 * Ghomala, Lamnso) ont des bundles générés par IA, non relus par des locuteurs natifs.
 * Décision produit : les afficher quand même plutôt que de rester repliés sur le français.
 * À corriger dès qu'une relecture native est disponible pour chaque langue.
 */
const TRANSLATED_LANGUAGE_CODES = new Set(['fr', 'en', 'bass', 'dua', 'ewo', 'ful', 'bum', 'bul', 'gha', 'lam']);

/**
 * Langues affichées dans le sélecteur. Les 8 langues nationales restent traduites et
 * sélectionnables par code (URL, localStorage) mais sont masquées du menu tant que leurs bundles
 * IA n'ont pas été relus par un locuteur natif — à retirer dès que ce travail est fait pour
 * ré-exposer la liste complète (TRANSLATED_LANGUAGE_CODES).
 */
const DISPLAYED_LANGUAGE_CODES = new Set(['fr', 'en']);

/**
 * Locale BCP-47 (Intl.DateTimeFormat) par langue applicative ayant une vraie donnée Intl.
 * Les langues nationales camerounaises n'ont pas de locale Intl reconnue par les navigateurs :
 * `localeCode` retombe alors sur 'fr-FR', cohérent avec `hasTranslation`/le repli déjà appliqué
 * par App#constructor pour le texte (voir effect() sur `translate.use`).
 */
const LOCALE_CODES: Record<string, string> = { fr: 'fr-FR', en: 'en-GB' };

/**
 * Le catalogue backend (referential-service, groupe LANGUAGES) laisse `iso6391` vide (ou
 * incohérent, ex. Fulfulde="ff") pour la plupart des langues nationales : impossible d'en dériver
 * de façon fiable le code applicatif attendu par public/i18n/*.json. On mappe donc explicitement
 * le code référentiel backend (item.code, ex. "BASSA") vers le code applicatif ("bass").
 */
const BACKEND_CODE_TO_APP_CODE: Record<string, string> = {
  FR: 'fr',
  EN: 'en',
  BASSA: 'bass',
  DUALA: 'dua',
  EWONDO: 'ewo',
  FULFULDE: 'ful',
  BAMOUN: 'bum',
  BULU: 'bul',
  GHOMALA: 'gha',
  LAMNSO: 'lam',
};

/** metadataJson de ItemResponse pour le groupe LANGUAGES (voir ReferentialService). */
interface LanguageMetadata {
  iso6391?: string;
  languageType?: 'OFFICIAL' | 'NATIONAL' | 'WORKING' | 'LOCAL';
  selectable?: boolean;
}

/**
 * UC-02 : catalogue des langues (referential-service, groupe LANGUAGES).
 *
 * GET /referentials/languages (endpoint dédié le plus riche) exige un JWT côté backend alors
 * que ce sélecteur doit fonctionner pour un visiteur anonyme (bug backend) : on utilise donc
 * /referentials/groups/LANGUAGES/items, qui est public et porte les mêmes données dans metadataJson.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly referentialService = inject(ReferentialService);

  readonly languages = signal<LanguageOption[]>(FALLBACK_LANGUAGES.filter((lang) => DISPLAYED_LANGUAGE_CODES.has(lang.code)));

  private readonly currentLangSignal = signal<AppLanguage>(
    this.readStoredLanguage(),
  );

  readonly currentLang = this.currentLangSignal.asReadonly();
  readonly currentLanguageIsTranslated = computed(() => TRANSLATED_LANGUAGE_CODES.has(this.currentLangSignal()));

  /** Locale Intl (dates, nombres…) de la langue réellement active — voir `LOCALE_CODES`. */
  readonly localeCode = computed(() => {
    const lang = this.currentLangSignal();
    const active = this.hasTranslation(lang) ? lang : 'fr';
    return LOCALE_CODES[active] ?? 'fr-FR';
  });

  constructor() {
    this.referentialService.groupItems('LANGUAGES').subscribe({
      next: (items) => {
        const options = items
          .map((item) => ({ item, metadata: this.parseMetadata(item.metadataJson) }))
          .filter(({ metadata }) => metadata.selectable !== false)
          .map(({ item, metadata }) => ({
            code: BACKEND_CODE_TO_APP_CODE[item.code.toUpperCase()] ?? (metadata.iso6391 || item.code).toLowerCase(),
            label: item.label,
          }))
          .filter((lang) => DISPLAYED_LANGUAGE_CODES.has(lang.code));
        if (options.length > 0) {
          const labels = new Map(options.map((option) => [option.code, option.label]));
          this.languages.set(
            FALLBACK_LANGUAGES
              .filter((language) => DISPLAYED_LANGUAGE_CODES.has(language.code))
              .map((language) => ({ ...language, label: labels.get(language.code) ?? language.label })),
          );
        }
      },
      // Le repli statique (FALLBACK_LANGUAGES) reste en place si le catalogue est indisponible.
      error: () => undefined,
    });
  }

  hasTranslation(code: AppLanguage): boolean {
    return TRANSLATED_LANGUAGE_CODES.has(code);
  }

  setLanguage(lang: AppLanguage): void {
    if (!DISPLAYED_LANGUAGE_CODES.has(lang) || !TRANSLATED_LANGUAGE_CODES.has(lang)) return;
    if (this.isBrowser && typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, lang);
    }
    this.currentLangSignal.set(lang);
  }

  private parseMetadata(metadataJson: string | null): LanguageMetadata {
    if (!metadataJson) return {};
    try {
      return JSON.parse(metadataJson) as LanguageMetadata;
    } catch {
      return {};
    }
  }

  private readStoredLanguage(): AppLanguage {
    const stored = this.isBrowser && typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    return stored && DISPLAYED_LANGUAGE_CODES.has(stored) && TRANSLATED_LANGUAGE_CODES.has(stored) ? stored : 'fr';
  }
}
